import MangaNavbar from "@/components/feature/MangaNavbar";
import MangaFooter from "@/components/feature/MangaFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/MangaBreadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import ContactForm from "./components/ContactForm";

const values = [
  {
    icon: "ri-eye-line",
    title: "每天看得见",
    desc: "坚持每日更新，热门连载最新话第一时间上线，让追更成为一种确定的期待。",
  },
  {
    icon: "ri-heart-line",
    title: "为你而选",
    desc: "从海量作品中甄选人气与口碑俱佳的连载，帮你省去大海捞针的时间。",
  },
  {
    icon: "ri-community-line",
    title: "与读者同行",
    desc: "重视每一位读者的反馈，持续打磨追更体验，和大家一起见证好故事的成长。",
  },
];

const principles = [
  { title: "真实及时", desc: "更新信息真实可靠，最新话进度清晰可查，不做夸大宣传。" },
  { title: "尊重创作", desc: "尊重作者与汉化组的辛勤付出，倡导理性、友善的追更氛围。" },
  { title: "简洁易用", desc: "界面清爽、操作简单，让你把精力放在看漫画而不是研究网站。" },
  { title: "持续进化", desc: "根据读者建议不断优化分类、榜单与提醒功能，让追更体验越来越好。" },
];

const testimonials = [
  { name: "追更的小满", role: "国漫爱好者", text: "每天起床第一件事就是看天天漫画网更没更新，九霄破从不让我失望，更新提醒特别及时！" },
  { name: "Lemon 酱", role: "日漫读者", text: "分类很清晰，日漫最新话上线得快，追更几部连载都不用担心错过剧情了。" },
  { name: "阿哲", role: "上班族", text: "周末才有空刷漫画，收藏功能帮了大忙，复更了会提醒我，超贴心。" },
  { name: "水墨", role: "资深漫迷", text: "榜单维度多，热度、评分、收藏都能看，找新坑方便多了，已经安利给好几个朋友。" },
];

const breadcrumbItems = [{ label: "首页", to: "/" }, { label: "关于我们" }];

export default function About() {
  useSeo({
    title: "关于我们 - 天天漫画网品牌介绍与理念",
    description:
      "了解天天漫画网：一个每日更新海量人气连载漫画的追更平台，坚持真实及时、尊重创作、简洁易用、持续进化的内容原则，与读者一起见证好故事。",
    keywords: "天天漫画网关于我们,漫画追更平台,每日更新漫画,漫画网站理念,追更口碑",
    canonicalPath: "/about",
  });

  const aboutData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于天天漫画网",
    url: "https://panelpeek.github.io/about",
    inLanguage: "zh-CN",
    description: "天天漫画网是一个每日更新海量人气连载漫画的追更平台。",
    mainEntity: {
      "@type": "Organization",
      name: "天天漫画网",
      url: "https://panelpeek.github.io/",
      contactPoint: {
        "@type": "ContactPoint",
        email: "contact@tiantian-manhua.example.com",
        contactType: "customer service",
      },
    },
  };

  return (
    <>
      <JsonLd id="about-jsonld" data={aboutData} />
      <MangaNavbar />
      <Breadcrumb items={breadcrumbItems} />
      <main>
        <section className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl">
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950">
                让每一次追更，<span className="text-primary-500">都不落空</span>
              </h1>
              <p className="mt-5 text-sm md:text-base text-foreground-600 leading-7">
                天天漫画网是一个专注连载漫画每日更新的追更平台。我们相信，好的故事值得被及时看到。无论你是喜欢热血国漫，还是钟情日漫连载，
                天天漫画网都用每日持续更新的方式，把最新剧情第一时间送到你面前，追更不迷路。
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 bg-primary-100 px-3 py-1 rounded-full">
                <i className="ri-compass-3-line"></i>网站定位
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl text-foreground-950">我们想做的三件事</h2>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
              {values.map((v) => (
                <div key={v.title} className="rounded-2xl bg-background-50 border border-background-200 p-6 text-center">
                  <span className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-500 text-background-50 flex items-center justify-center mx-auto">
                    <i className={`${v.icon} text-2xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg text-foreground-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-6">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-700 bg-secondary-100 px-3 py-1 rounded-full">
                <i className="ri-flag-2-line"></i>内容原则
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl text-foreground-950">我们坚持的原则</h2>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p, idx) => (
                <div key={p.title} className="rounded-2xl bg-background-100 border border-background-200 p-6">
                  <span className="font-heading text-3xl text-accent-500">{String(idx + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 text-base font-medium text-foreground-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-6">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-secondary-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-700 bg-secondary-100 px-3 py-1 rounded-full">
                <i className="ri-chat-3-fill"></i>读者口碑
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl text-foreground-950">追更读者怎么说</h2>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-2xl bg-background-50 border border-background-200 p-6">
                  <div className="flex gap-0.5 text-accent-500">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm text-foreground-700 leading-6">“{t.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs font-medium">
                      {t.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{t.name}</span>
                      <span className="block text-xs text-foreground-400">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-8 md:gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-700 bg-accent-100 px-3 py-1 rounded-full">
                <i className="ri-customer-service-2-line"></i>联系我们
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl text-foreground-950">和我们聊聊</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-7">
                无论是追更建议、作品投稿，还是版权与合作事宜，都欢迎随时与我们取得联系。我们重视每一位读者的声音，会在 1-2 个工作日内回复。
              </p>

              <div className="mt-7 space-y-4">
                <a href="mailto:contact@tiantian-manhua.example.com" className="flex items-center gap-4 rounded-2xl bg-background-100 border border-background-200 p-4 hover:border-primary-300 transition-colors">
                  <span className="w-11 h-11 rounded-xl bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-400">邮箱</div>
                    <div className="text-sm font-medium text-foreground-900">contact@tiantian-manhua.example.com</div>
                  </div>
                </a>
                <div className="flex items-center gap-4 rounded-2xl bg-background-100 border border-background-200 p-4">
                  <span className="w-11 h-11 rounded-xl bg-secondary-500 text-background-50 flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-400">更新时段</div>
                    <div className="text-sm font-medium text-foreground-900">每日 08:00 - 18:00 最新话陆续上线</div>
                  </div>
                </div>
              </div>
            </div>

            <ContactForm />
          </div>
        </section>
      </main>
      <MangaFooter />
      <BackToTop />
    </>
  );
}